import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { 
  ArrowLeftIcon,
  FolderIcon
} from '@heroicons/react/24/outline'
import { GET_CATEGORIES, GET_CATEGORY } from '../graphql/product-queries'
import { UPDATE_CATEGORY } from '../graphql/product-mutations'
import { UpdateCategoryData, Category, PlatformAvailability } from '../types/product'
import LoadingSpinner from '../components/LoadingSpinner'
import PlatformPicker from '../components/PlatformPicker'
import toast from 'react-hot-toast'

const EditCategoryPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  // removed unused client
  const [formData, setFormData] = useState<UpdateCategoryData>({
    name: '',
    parent_id: undefined,
    active: true,
    display_in_pos: true,
    display_in_webshop: true,
  })
  const [enabledSlugs, setEnabledSlugs] = useState<Set<string>>(new Set())

  const handlePlatformChange = (slug: string, enabled: boolean) => {
    setEnabledSlugs(prev => {
      const next = new Set(prev)
      if (enabled) next.add(slug)
      else next.delete(slug)
      return next
    })
  }

  const { data: categoriesData, loading: categoriesLoading } = useQuery(GET_CATEGORIES, {
    variables: { filters: {} }
  })

  const { data: categoryData, loading: categoryLoading } = useQuery(GET_CATEGORY, {
    variables: { id: parseInt(id!) },
    skip: !id
  })

  const categories = categoriesData?.categories || []
  const category = categoryData?.category

  const [updateCategory, { loading: updateLoading }] = useMutation(UPDATE_CATEGORY, {
    onCompleted: (data) => {
      toast.success('Category updated successfully!')
      navigate(`/categories/${data.updateCategory.id}`)
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update category')
    },
    update: (cache, { data }) => {
      if (data?.updateCategory) {
        // Update the categories list cache
        const existingCategories: any = cache.readQuery({
          query: GET_CATEGORIES,
          variables: { filters: {} }
        })

        if (existingCategories) {
          const updatedCategories = existingCategories.categories.map((cat: any) =>
            cat.id === data.updateCategory.id ? data.updateCategory : cat
          )
          
          cache.writeQuery({
            query: GET_CATEGORIES,
            variables: { filters: {} },
            data: {
              categories: updatedCategories
            }
          })
        }
      }
    }
  })

  // Populate form when category data loads
  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        parent_id: category.parent?.id,
        active: category.active,
        display_in_pos: category.display_in_pos,
        display_in_webshop: category.display_in_webshop,
      })
      const initial = new Set<string>(
        (category.platforms ?? [])
          .filter((a: PlatformAvailability) => a.enabled)
          .map((a: PlatformAvailability) => a.platform.slug)
      )
      setEnabledSlugs(initial)
    }
  }, [category])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name?.trim()) {
      toast.error('Category name is required')
      return
    }

    updateCategory({
      variables: {
        id: parseInt(id!),
        categoryData: {
          ...formData,
          name: formData.name.trim(),
          parent_id: formData.parent_id || undefined,
          platformSlugs: Array.from(enabledSlugs)
        }
      }
    })
  }

  const handleInputChange = (field: keyof UpdateCategoryData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  if (categoriesLoading || categoryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!category) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">❌</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Category not found</h3>
        <p className="text-gray-500 mb-6">The category you're looking for doesn't exist.</p>
        <Link to="/categories" className="btn-primary">
          Back to Categories
        </Link>
      </div>
    )
  }

  // Filter out the current category and its descendants from parent options
  const getAvailableParentOptions = () => {
    const isDescendant = (parentId: string, targetId: string): boolean => {
      const parent = categories.find((cat: Category) => cat.id === Number(parentId))
      if (!parent) return false
      if (parent.id === targetId) return true
      return isDescendant(parent.parent?.id || '', targetId)
    }

    return categories.filter((cat: Category) => 
      cat.id !== category.id && !isDescendant(String(cat.id), String(category.id))
    )
  }

  const availableParents = getAvailableParentOptions()

  const availabilities: PlatformAvailability[] = (category.platforms ?? []).map(
    (a: PlatformAvailability) => ({
      platform: a.platform,
      enabled: enabledSlugs.has(a.platform.slug),
    })
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to={`/categories/${category.id}`}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Edit Category</h1>
          <p className="text-sm text-gray-500">Update category information</p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Category Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter category name"
                  required
                />
              </div>

              <div>
                <label htmlFor="parent_id" className="block text-sm font-medium text-gray-700">
                  Parent Category
                </label>
                                 <select
                   id="parent_id"
                   value={formData.parent_id || ''}
                   onChange={(e) => handleInputChange('parent_id', e.target.value ? Number(e.target.value) : undefined)}
                   className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                   disabled={categoriesLoading}
                 >
                   <option value="">No parent category (top level)</option>
                  {availableParents.map((parent: any) => (
                     <option key={parent.id} value={parent.id}>
                       {parent.name}
                     </option>
                   ))}
                 </select>
                <p className="mt-1 text-sm text-gray-500">
                  Select a parent category to create a hierarchy, or leave empty for a top-level category
                </p>
              </div>

              <div className="flex items-center space-x-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => handleInputChange('active', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
                    Active
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="display_in_pos"
                    checked={formData.display_in_pos}
                    onChange={(e) => handleInputChange('display_in_pos', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="display_in_pos" className="ml-2 block text-sm text-gray-700">
                    Display in POS
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="display_in_webshop"
                    checked={formData.display_in_webshop}
                    onChange={(e) => handleInputChange('display_in_webshop', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="display_in_webshop" className="ml-2 block text-sm text-gray-700">
                    Display in Webshop
                  </label>
                </div>
              </div>

              <div className="pt-3 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Available on delivery platforms
                </label>
                <PlatformPicker
                  availabilities={availabilities}
                  onChange={handlePlatformChange}
                  showSelectAll
                  helperText="Products in this category are pushed to a platform only if both the category and the product itself have it enabled."
                />
              </div>
            </div>
          </div>

          {/* Current Category Info */}
          <div className="card bg-gray-50">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Current Category Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Current Parent:</span>
                <span className="ml-2 text-gray-900">
                  {category.parent?.name || 'No parent category'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Subcategories:</span>
                <span className="ml-2 text-gray-900">{category.children?.length || 0}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Products:</span>
                <span className="ml-2 text-gray-900">{category.products?.length || 0}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Status:</span>
                <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                  category.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {category.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Warning for Parent Changes */}
          {category.children && category.children.length > 0 && (
            <div className="card bg-yellow-50 border border-yellow-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <FolderIcon className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Category has subcategories
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      This category has {category.children.length} subcategory{category.children.length !== 1 ? 'ies' : ''}. 
                      Changing the parent category may affect the hierarchy structure.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3">
            <Link
              to={`/categories/${category.id}`}
              className="btn-secondary"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={updateLoading || !formData.name?.trim()}
              className="btn-primary"
            >
              {updateLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  Updating...
                </>
              ) : (
                'Update Category'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditCategoryPage
